import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { AppButton, AppInput, AppTextarea, Divider, Typography, LoadingSpinner } from "@libs/zapi-ui/custom";
import { BuildingIcon, UploadSimpleIcon, TrashSimpleIcon } from "@phosphor-icons/react";
import { useRef, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../const/const";
import { toast } from "sonner";
import { useUpdateOrganization } from "../services/useUpdateOrganization";
import { useDeleteOrganizationLogo } from "../services/useDeleteOrganizationLogo";
import { useAiModelSettings } from "../services/useAiModelSettings";
import { useOrganization } from "../services/useOrganization";

type OrganizationData = {
  organizationName: string;
  customerInstructions: string;
  organizationLogoUrl: string | null;
};

function AIModelSection() {
  const { aiModelSettings, isLoading } = useAiModelSettings();
  const { isLabs } = useMeStore();

  if (isLoading) return null;
  if (!aiModelSettings) return null;
  if (isLabs) return null;

  const { isUsingOrganizationOverride, currentModel } = aiModelSettings;

  const displayValue = isUsingOrganizationOverride ? currentModel : "Innovera Default";

  return (
    <div className="flex flex-col gap-1">
      <AppInput label="AI Model" className="w-[320px]" value={displayValue} disabled={true} />
    </div>
  );
}

export function GeneralSection() {
  const { organizationId: userOrganizationId, isLabs } = useMeStore();
  const { currentOrganizationId, updateOrganizationName } = useOrganizationStore();
  const { data: organizationData, refetch: refetchOrganization, isLoading: isLoadingOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use currentOrganizationId if available (for superAdmin), otherwise fall back to user's organizationId
  const organizationId = currentOrganizationId || userOrganizationId;

  const { mutate: updateOrganization, isPending: isSaving } = useUpdateOrganization();
  const { mutate: deleteOrganizationLogo, isPending: isDeleting } = useDeleteOrganizationLogo();

  const [orgName, setOrgName] = useState("");
  const [customerInstructionsValue, setCustomerInstructionsValue] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (organizationData) {
      setOrgName(organizationData.organizationName ?? "");
    }
  }, [organizationData]);

  useEffect(() => {
    if (organizationData) {
      setCustomerInstructionsValue(organizationData.customerInstructions);
    }
  }, [organizationData]);

  const isChanged =
    orgName !== (organizationData?.organizationName ?? "") ||
    customerInstructionsValue !== (organizationData?.customerInstructions ?? "");

  function fileClickHandler() {
    fileInputRef.current?.click();
  }

  function validateFile(file: File): string | null {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast.error("Invalid file type. Only JPG, PNG and WebP files are allowed.");
      return "Invalid file type. Only JPG, PNG and WebP files are allowed.";
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must not exceed 5MB.");
      return "File size must not exceed 5MB.";
    }

    return null;
  }

  async function fileChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleRemovePhoto = async () => {
    if (!organizationId) {
      toast.error("Organization ID not found");
      return;
    }

    setUploadError(null);

    deleteOrganizationLogo(
      { organizationId },
      {
        onSuccess: async () => {
          // Optimistically update query cache to remove logo URL
          queryClient.setQueryData<OrganizationData>(["organization", currentOrganizationId], (old) => {
            if (!old) return old;
            return { ...old, organizationLogoUrl: null };
          });

          toast.success("Organization logo removed successfully");
          await refetchOrganization();
        },
        onError: (error) => {
          console.error("Failed to remove organization logo:", error);
          const err = error as { response?: { data?: { message?: string } } };
          setUploadError(err.response?.data?.message || "Failed to remove logo. Please try again.");
          toast.error("Failed to remove logo. Please try again.");
        }
      }
    );
  };

  const handleSaveChanges = async () => {
    if (!organizationId) {
      toast.error("Organization ID not found");
      return;
    }

    setUploadError(null);

    updateOrganization(
      {
        organizationId,
        name: orgName,
        file: selectedFile ?? undefined,
        customerInstructions: customerInstructionsValue
      },
      {
        onSuccess: async () => {
          // Optimistically update query cache immediately to reset isChanged
          // Note: Logo URL will be updated after refetch, but we update name and instructions immediately
          queryClient.setQueryData<OrganizationData>(["organization", currentOrganizationId], (old) => {
            if (!old) return old;
            return {
              ...old,
              organizationName: orgName,
              customerInstructions: customerInstructionsValue
            };
          });

          // Update organization name in the organization store (for the dropdown)
          if (organizationId) {
            updateOrganizationName(organizationId, orgName);
          }

          // Update local state immediately with saved values
          setOrgName(orgName);
          setCustomerInstructionsValue(customerInstructionsValue);

          toast.success("Organization updated successfully");
          await refetchOrganization();

          setSelectedFile(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
        onError: (error) => {
          toast.error("Failed to update organization. Please try again.");
          console.error("Failed to update organization:", error);
        }
      }
    );
  };

  if (isLoadingOrganization) {
    return (
      <div className="flex flex-col gap-[18px] bg-white">
        <Typography variant="heading6" color="zinc900">
          General
        </Typography>
        <div className="flex items-center gap-2">
          <LoadingSpinner />
          <Typography variant="body3" color="zinc500">
            Loading organization data...
          </Typography>
        </div>
      </div>
    );
  }

  const organizationLogoUrl = organizationData?.organizationLogoUrl || null;

  return (
    <div className="flex flex-col gap-[18px] bg-white">
      <Typography variant="heading6" color="zinc900">
        General
      </Typography>

      <div className="flex flex-col gap-8">
        <div className="flex gap-6">
          <button
            className={cn(
              "border-none outline-none cursor-pointer flex items-center justify-center",
              "w-[88px] h-[88px] rounded-full bg-innoveraZinc-900 overflow-hidden"
            )}
            onClick={fileClickHandler}
          >
            {(previewUrl || organizationLogoUrl) && (
              <img
                src={previewUrl || organizationLogoUrl || ""}
                alt="organization logo"
                className="w-full h-full object-cover"
              />
            )}
            {!previewUrl && !organizationLogoUrl && <BuildingIcon className="text-white w-[35px] h-[35px]" />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={fileChangeHandler}
          />

          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <AppButton
                appearance="contained"
                color="zinc100"
                startAdornment={<UploadSimpleIcon />}
                onClick={fileClickHandler}
                disabled={isSaving || isDeleting}
              >
                Upload Logo
              </AppButton>
              {organizationLogoUrl && (
                <AppButton
                  appearance="text"
                  startAdornment={<TrashSimpleIcon />}
                  onClick={handleRemovePhoto}
                  disabled={isSaving}
                  isLoading={isDeleting}
                  loadingText="Removing..."
                >
                  Remove Logo
                </AppButton>
              )}
            </div>
            <div>
              <Typography variant="body3" color="zinc500">
                For best results, upload a square image (400x400 px)
              </Typography>
              <Typography variant="body3" color="zinc500">
                JPG, PNG or WebP format, up to 5MB
              </Typography>
              {uploadError && (
                <Typography variant="body3" className="text-red-500 mt-1">
                  {uploadError}
                </Typography>
              )}
            </div>
          </div>
        </div>

        <Divider className="!bg-innoveraZinc-100" />

        <div className="flex gap-4">
          <AppInput
            label="Organization Name"
            className="w-[250px]"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            disabled={isSaving || isDeleting}
          />
        </div>

        <AIModelSection />

        {isLabs && (
          <div className="flex flex-col gap-1">
            <AppTextarea
              label="Organization Instructions"
              className="w-full min-w-[800px] max-w-[800px] min-h-[200px]"
              value={customerInstructionsValue}
              onChange={(e) => setCustomerInstructionsValue(e.target.value)}
              disabled={isSaving || isDeleting}
              rows={6}
              placeholder="Type your instructions here"
            />
          </div>
        )}

        <AppButton
          className="w-fit"
          onClick={handleSaveChanges}
          disabled={isSaving || isDeleting || !isChanged}
          isLoading={isSaving}
          loadingText="Saving..."
        >
          Save changes
        </AppButton>
      </div>
    </div>
  );
}
